import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createAppThunk, type IRes } from "../common";
import { type IResLogin, type ILoginFetc, type ITokenInfo, Grade, type IResPassWord } from "./authVo";
import Http from "commons/Http";
import { API_USER } from "config/api";
import { rdxSetCookie } from "commons/cookies/cookiesR";
import { CookieName } from "../../storageVo";
import { rdxSetLeftlist } from "../navigates/navigatesR";
import { ActiveType } from "pages/calculate/store/CalculateVo";
// 이름 설정
const name = "login";

export interface IAuth {
  id: number;
  manager_name: string;
  isLogin: boolean;
  name: string;
  role: string;
  type: string;
  email: string;
  token: string;
  gradeList?: Array<{ code: string; name: string; value?: string }>;
  cp_code?: string;
  view_type?: string;
  service_type?: string[];
  serviceList?: Array<{ id: string; text: string }>;
  channel?: {
    list: { [key: string]: { id: number; text: string; value: string }[] };
    selected: { [key: string]: { code: string; text: string }[] };
  };

  kakao: {
    client_id: string;
    token?: string;
  };
  guide: {
    viewFlag?: boolean;
    step?: number;
  };
}

export const apiGtokenInfo = createAppThunk<ITokenInfo>(`${name}/apiGtokenInfo`, async (_, thunkApi) => {
  const res = await Http.get<IRes<ITokenInfo>>(`${API_USER}/token`);
  if (res.data.code === 200 && res.data.content) {
    thunkApi.dispatch(
      rdxSetUserData({
        id: res.data.content.data.id,
        name: res.data.content.data.cp_name,
        email: res.data.content.data.email,
        role: res.data.content.data.role,
        manager_name: res.data.content.data.manager_name,
        service_type_list: res.data.content.data.service_type_list,
      }),
    );
    thunkApi.dispatch(
      rdxSetLeftlist({
        grade: res.data.content.data.role as Grade,
        serviceTypeList: res.data.content.data.service_type_list,
      }),
    );
    thunkApi.dispatch(rdxSetGuideFlag(res.data.content.data.guide_flag ? res.data.content.data.guide_flag : false));
  }
  return res.data;
});

export const apiGtokenRefesh = createAppThunk<{ access_token: string; expire_date: string }>(
  `${name}/apiGtokenRefresh`,
  async () => {
    const res = await Http.get<IRes<{ access_token: string; expire_date: string }>>(`${API_USER}/refresh`);

    if (res.data.code === 200) {
      document.cookie = `${CookieName.REFRESH}=${res.data.content?.access_token as string};max-age=${
        res.data.content?.expire_date
      };path=/;`;
    }
    return res.data;
  },
);

export const apiPlogin = createAppThunk<IResLogin, ILoginFetc>(`${name}/apiPlogin`, async (params, thunkApi) => {
  const res = await Http.post<IRes<IResLogin>>(`${API_USER}/login`, params);
  if (res.data.code === 200) {
    thunkApi.dispatch(
      rdxSetCookie({
        name: CookieName.AUTH,
        value: res.data.content?.data.access_token as string,
        expire: res.data.content?.data.expire_date as string,
      }),
    );
  }
  return res.data;
});

export const apiPpassword = createAppThunk<IResPassWord>(`${name}/apiPpassword`, async (_, thunkApi) => {
  const uiInput = thunkApi.getState().ui.inputText;
  const res = await Http.post<IRes<IResPassWord>>(`${API_USER}/password`, {
    password: uiInput!["password_id"],
  });
  return res.data;
});

export const apiPkakaoLogin = createAppThunk<IResLogin, string>(`${name}/apiPkakaoLogin`, async (param, thunkApi) => {
  const res = await Http.post<IRes<IResLogin>>(`${API_USER}/login/kakao`, {
    token: thunkApi.getState().auth.kakao.token,
  });

  if (res.data.code === 200) {
    thunkApi.dispatch(
      rdxSetCookie({
        name: CookieName.AUTH,
        value: res.data.content?.data.access_token as string,
        expire: res.data.content?.data.expire_date as string,
      }),
    );
  }

  return res.data;
});

export const apiPguideViewFlag = createAppThunk(`${name}/apiPguideViewFlag`, async (_, thunkApi) => {
  const res = await Http.post(`${API_USER}/flag`, {
    guide_flag: true,
  });
  return res.data;
});

// data 를 관리하는 reducer 설정
const loginSlice = createSlice({
  name,
  initialState: {
    isLogin: false,
    name: "",
    role: "",
    email: "",
    kakao: {
      client_id: "599ce8136c1cf66496e44d18b446d8fa",
    },
    guide: {},
  } as IAuth,
  reducers: {
    rdxSetUserData(
      state: IAuth,
      action: PayloadAction<{
        id: number;
        name: string;
        email: string;
        role: string;
        manager_name: string;
        service_type_list?: string[];
      }>,
    ) {
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.email = action.payload.email;
      state.role = action.payload.role;
      state.manager_name = action.payload.manager_name;
      state.isLogin = true;

      const serviceList = action.payload.service_type_list;
      state.service_type = serviceList;

      const list = serviceList?.filter(i => i === ActiveType.CP || i === ActiveType.CON || i === ActiveType.PN);
      state.serviceList = list?.map(i => {
        let text;
        switch (i) {
          case "PROVIDER":
            text = "콘텍스트";
            break;
          case "MEDIA":
            text = "음원";
            break;
          case "PARTNERS":
            text = "파트너스";
            break;
          case "IP_PROVIDER":
          case "IP_PARTNERS":
            text = "IP";
            break;
          default:
            text = "";
            break;
        }
        return { id: i, text: text };
      });
    },

    rdxGradeList(state: IAuth, action: PayloadAction<Array<{ code: string; name: string; value?: string }>>) {
      state.gradeList = action.payload;
      state.manager_name = action.payload[0].name;
      state.name = action.payload[0].name;
      state.cp_code = action.payload[0].code;
      state.view_type = action.payload[0].value;
    },
    rdxUpdateUserInfo(state: IAuth, action: PayloadAction<{ code: string; name: string; value?: string }>) {
      // state.manager_name = action.payload.name;
      state.name = action.payload.name;
      state.cp_code = action.payload.code === "all" ? "all" : action.payload.code;
      state.view_type = action.payload.value ? action.payload.value : "all";
    },

    rdxSetKakaoToken(state: IAuth, action: PayloadAction<string>) {
      state.kakao.token = action.payload;
    },

    rdxSetGuideFlag(state: IAuth, action: PayloadAction<boolean>) {
      state.guide.viewFlag = action.payload;
    },
    rdxSetGuideStep(state: IAuth, action: PayloadAction<number>) {
      state.guide.step = action.payload;
    },
  },
  // extraReducers: builder => {
  //   builder.addCase(apiPlogin.rejected, (state: IAuth, action) => {
  //     console.log("실패", state, action);
  //   });
  // },
});

/**
 * CSR 에서 쓰는 액션들
 */
export const { rdxSetUserData, rdxGradeList, rdxUpdateUserInfo, rdxSetKakaoToken, rdxSetGuideFlag, rdxSetGuideStep } =
  loginSlice.actions;
// data 를 관리하는 reducer 기본 반환 설정
export default loginSlice.reducer;
